import { useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Box from '@mui/material/Box'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'

import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

const navLinks = [
  { label: 'Why It Matters', to: '/benefits' },
  { label: 'Research Library', to: '/research' },
  { label: 'Directory', to: '/directory' },
  { label: 'About', to: '/about' },
]

export default function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/'
    return location.pathname.startsWith(to)
  }

  return (
    <>
      <AppBar position="sticky" elevation={0} sx={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Toolbar sx={{ gap: 1, py: 0.5 }}>
          {/* Logo */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textDecoration: 'none',
              color: 'inherit',
              flexGrow: isMobile ? 1 : 0,
              mr: isMobile ? 0 : 4,
            }}
          >
            <Box sx={{ width: 26, height: 26, borderRadius: '50%', border: '3px solid #B87333', flexShrink: 0 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"Space Grotesk", "Helvetica Neue", sans-serif',
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.15rem' },
                letterSpacing: '-0.01em',
                lineHeight: 1.15,
                color: '#B87333',
              }}
            >
              Future of Farm Food
            </Typography>
          </Box>

          {/* Desktop nav */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
              {navLinks.map((link) => (
                <Button
                  key={link.to}
                  component={RouterLink}
                  to={link.to}
                  color="inherit"
                  sx={{
                    opacity: isActive(link.to) ? 1 : 0.82,
                    backgroundColor: isActive(link.to) ? 'rgba(255,255,255,0.14)' : 'transparent',
                    borderRadius: 999,
                    px: 2,
                    py: 0.8,
                    fontSize: '0.875rem',
                    '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.16)' },
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>
          )}

          {/* CTA button */}
          {!isMobile && (
            <Button
              component={RouterLink}
              to="/submit"
              variant="contained"
              size="small"
              endIcon={<ArrowForwardIcon sx={{ fontSize: '1rem !important' }} />}
              sx={{
                ml: 1,
                px: 2.5,
                py: 0.9,
                borderRadius: 999,
                backgroundColor: '#B87333',
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '0.85rem',
                letterSpacing: '0.01em',
                boxShadow: '0 4px 14px rgba(184,115,51,0.45)',
                border: 'none',
                transition: 'background-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease',
                '&:hover': {
                  backgroundColor: '#D4924D',
                  boxShadow: '0 6px 18px rgba(184,115,51,0.55)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Submit Organisation
            </Button>
          )}

          {/* Mobile hamburger */}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="end"
              onClick={() => setDrawerOpen(true)}
              aria-label="open menu"
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 280, backgroundColor: 'background.default' } }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={() => setDrawerOpen(false)} aria-label="close menu">
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {navLinks.map((link) => (
            <ListItem key={link.to} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={link.to}
                onClick={() => setDrawerOpen(false)}
                selected={isActive(link.to)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(138, 154, 91, 0.15)',
                    color: 'primary.main',
                    fontWeight: 600,
                  },
                }}
              >
                <ListItemText primary={link.label} />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItem disablePadding sx={{ mt: 2, px: 2 }}>
            <Button
              component={RouterLink}
              to="/submit"
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => setDrawerOpen(false)}
            >
              Submit Organisation
            </Button>
          </ListItem>
        </List>
      </Drawer>
    </>
  )
}
